import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { AuthService } from '../../../../auth-pages/services/auth.service';
import { MeetingAvailabilityOverrideApiService } from '../../../services/meeting-availability-override-api.service';
import {
  MeetingUserAvailabilityOverrideRequest,
  MeetingUserAvailabilityOverrideResponse,
} from '../../../models/meeting.model';

@Component({
  selector: 'app-availability-overrides-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './availability-overrides-form.component.html',
})
export class AvailabilityOverridesFormComponent implements OnInit {
  @Input() drawerMode = true;
  @Input() prefilledIdUser?: number;

  @Output() saved = new EventEmitter<MeetingUserAvailabilityOverrideResponse>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  overrideId: number | null = null;
  currentIdUser: number | null = null;

  constructor(
    private fb: FormBuilder,
    private api: MeetingAvailabilityOverrideApiService,
    private authService: AuthService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    // Drawer wrapper invoca reset()/loadOverride() al abrir.
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  private timeString(v: any): string {
    if (!v) return '';
    if (typeof v === 'string') return v.slice(0, 5);
    const h = String(v.hour ?? 0).padStart(2, '0');
    const m = String(v.minute ?? 0).padStart(2, '0');
    return `${h}:${m}`;
  }

  private initForm(): void {
    this.form = this.fb.group({
      overrideDate: ['', [Validators.required]],
      isAvailable: [false],
      startTime: ['09:00'],
      endTime: ['17:00'],
      reason: ['', [Validators.maxLength(500)]],
    });
  }

  reset(): void {
    this.isEditing = false;
    this.overrideId = null;
    this.currentIdUser = this.prefilledIdUser ?? this.authService.getCurrentUser()?.id ?? null;
    this.error = '';
    this.saving = false;
    this.loading = false;
    this.initForm();
  }

  loadOverride(id: number): void {
    this.loading = true;
    this.isEditing = true;
    this.overrideId = id;
    this.api.getById(id, this.businessId).subscribe({
      next: (item) => {
        this.currentIdUser = item.idUser;
        this.form.patchValue({
          overrideDate: item.overrideDate,
          isAvailable: !!item.isAvailable,
          startTime: this.timeString(item.startTime) || '09:00',
          endTime: this.timeString(item.endTime) || '17:00',
          reason: item.reason || '',
        });
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la excepción';
        this.loading = false;
      },
    });
  }

  guardar(): void {
    if (this.form.invalid || !this.currentIdUser) {
      this.form.markAllAsTouched();
      if (!this.currentIdUser) this.error = 'Usuario no identificado';
      return;
    }
    this.saving = true;
    this.error = '';

    const v = this.form.value;
    const body: MeetingUserAvailabilityOverrideRequest = {
      idUser: this.currentIdUser,
      businessId: this.businessId,
      overrideDate: v.overrideDate,
      isAvailable: !!v.isAvailable,
      startTime: v.isAvailable && v.startTime ? `${v.startTime}:00` : undefined,
      endTime: v.isAvailable && v.endTime ? `${v.endTime}:00` : undefined,
      reason: v.reason?.trim() || undefined,
    };

    const req$ = this.isEditing && this.overrideId
      ? this.api.update(this.overrideId, this.businessId, body)
      : this.api.create(body);

    req$.subscribe({
      next: (item) => {
        this.saving = false;
        this.saved.emit(item);
      },
      error: (err) => {
        this.saving = false;
        this.error = err.error?.message || 'Error al guardar';
      },
    });
  }

  cancelar(): void {
    this.cancelled.emit();
  }

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return !!c && c.invalid && c.touched;
  }

  getError(field: string): string {
    const c = this.form.get(field);
    if (!c?.errors) return '';
    if (c.errors['required']) return 'Este campo es requerido';
    if (c.errors['maxlength']) return `Máximo ${c.errors['maxlength'].requiredLength} caracteres`;
    return 'Campo inválido';
  }
}
